import format from 'date-fns/format';
import { utcToZonedTime } from 'date-fns-tz';
import { AnimalTypeId } from '../types/pet';
import { SERVER_TIME_ZONE } from '../util/config';

export const PLACEHOLDER_TEXT = 'Not added yet';

export const PII_CLASSNAME = 'pii';

export type SFSGenderOptions = 'MALE' | 'FMLE' | 'UNKN';

export const isClient = () => typeof window !== 'undefined';

// check if userAgent is the Chewy iOS or Android app
const nmaUA = /chewy-android|chewy-ios/gi;

export const isNotChewyMobileApp = (userAgent?: string): boolean =>
  userAgent
    ? userAgent.match(nmaUA) == null
    : typeof navigator !== 'undefined' && navigator.userAgent.match(nmaUA) == null;

const CurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// can be deleted with https://chewyinc.atlassian.net/browse/PPF-2139
export const formattedPrice = (price?: number): string | undefined => {
  if (price && !Number.isNaN(price)) {
    return CurrencyFormatter.format(price);
  }
};

export const getDisplayBreed = (
  petProfile: { petBreedName: string; secondaryBreedName: string },
  showUnknown = false,
) => {
  const { petBreedName, secondaryBreedName } = petProfile;
  if (petBreedName === 'Mixed / Unknown' && !secondaryBreedName) {
    return 'Mixed';
  }
  if (
    (petBreedName.toLowerCase().includes('unknown') &&
      !petBreedName.toLowerCase().includes('mixed')) ||
    petBreedName === 'Other'
  ) {
    return showUnknown ? 'Unknown' : '';
  }

  let buildDisplayBreed = '';
  buildDisplayBreed += petBreedName === 'Mixed / Unknown' ? 'Mixed' : petBreedName;
  buildDisplayBreed += ' and ';
  buildDisplayBreed +=
    secondaryBreedName === 'Mixed / Unknown' || secondaryBreedName === 'Other'
      ? 'Mixed'
      : secondaryBreedName;

  return secondaryBreedName ? buildDisplayBreed : petBreedName;
};

export const isUnknownBreed = (breedName: string): boolean =>
  breedName === 'Mixed / Unknown' || breedName === 'Other';

export const getFormattedCelebrationDate = ({
  celebrationDate,
  formatTokens,
}: {
  celebrationDate?: number;
  formatTokens: string;
}) => {
  if (!celebrationDate) {
    return null;
  }
  return format(utcToZonedTime(celebrationDate, SERVER_TIME_ZONE), formatTokens);
};

// NOTE: Not actively being used but good to keep around as we might need it again
export const isPuppy = (
  petTypeId: number,
  ageInMonths: number,
  birthday?: number,
  providedTodayDate?: Date,
): boolean => {
  if (petTypeId !== AnimalTypeId.dog) {
    return false;
  }
  if (ageInMonths > 0 && ageInMonths < 12) {
    return true;
  }

  // puppy remains puppy for the whole birthday even if technically it's greater than 12 months age at midnight
  if (ageInMonths === 12 && birthday) {
    const birthDayDate = new Date(birthday);
    const todayDate = providedTodayDate || new Date();
    return (
      birthDayDate.getDate() === todayDate.getDate() &&
      birthDayDate.getMonth() === todayDate.getMonth()
    );
  }

  // in the case when someone creates a pet profile same month/day as the birthday
  if (ageInMonths === 0 && birthday) {
    return true;
  }
  return false;
};

// Use href if on Chewy mobile app to reload url, else use history.push
export const conditionalHistoryPush = (path: string, history) => {
  if (isNotChewyMobileApp()) {
    history.push(path);
  } else {
    window.location.href = path;
  }
};
